import { Box, Button, CheckIcon, Combobox, Group, InputBase, Text, useCombobox } from "@mantine/core"
import React, { useMemo, useState } from "react"
import { saveFields } from "../utils/fieldStorage"
import { Column } from "../types/analysisList"

interface Props {
  columns: Column[]
  selectedFields: string[]
  onChange: (fields: string[]) => void
  storageKey: string
  label: string
  showBulkActions?: boolean
}

export const ColumnsSelector: React.FC<Props> = ({
  columns, selectedFields, onChange, storageKey, label, showBulkActions = false,
}) => {
  const [search, setSearch] = useState('')
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setSearch('')
    },
  })

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return columns
    return columns.filter(
      c => c.headerName.toLowerCase().includes(q) || c.field.toLowerCase().includes(q),
    )
  }, [columns, search])

  // ユーザー操作で選択カラムを更新
  const updateSelection = (next: string[]) => {
    onChange(next)
    saveFields(storageKey, next)
  }

  const toggle = (field: string) => {
    updateSelection(
      selectedFields.includes(field) ?
        selectedFields.filter(f => f !== field) :
        [...selectedFields, field],
    )
  }

  return (
    <Group gap={8} align="flex-end">
      <Box>
        <Text fz={13} fw={600} c="#000000CC" mb={4}>{label}</Text>
        <Combobox
          store={combobox}
          onOptionSubmit={toggle}
          position="bottom-start"
          withinPortal={false}
        >
          <Combobox.Target>
            <InputBase
              component="button"
              type="button"
              pointer
              rightSection={<Combobox.Chevron />}
              rightSectionPointerEvents="none"
              onClick={() => combobox.toggleDropdown()}
              w={300}
            >
              {
                selectedFields.length > 0 ?
                  `${selectedFields.length}カラム選択中` :
                  <Text c="dimmed" fz="sm">カラムを選択してください</Text>
              }
            </InputBase>
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Search
              value={search}
              onChange={e => setSearch(e.currentTarget.value)}
              placeholder="カラム名を検索"
            />
            <Combobox.Options mah={300} style={{ overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <Combobox.Empty>該当するカラムがありません</Combobox.Empty>
              ) : (
                filtered.map(col => {
                  const checked = selectedFields.includes(col.field)
                  return (
                    <Combobox.Option value={col.field} key={col.field} active={checked}>
                      <Group gap="sm" wrap="nowrap">
                        {checked ? <CheckIcon size={12} /> : <Box w={12} />}
                        <span>{col.headerName}</span>
                      </Group>
                    </Combobox.Option>
                  )
                })
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </Box>

      {showBulkActions && (
        <Group gap={4}>
          <Button
            size="xs"
            variant="subtle"
            onClick={() => updateSelection(columns.map(c => c.field))}
          >
            すべてチェック
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => updateSelection([])}
          >
            すべて外す
          </Button>
        </Group>
      )}
    </Group>
  )
}
